import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  TextField,
  Switch,
  Button,
  CircularProgress,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CenterFocusStrongOutlinedIcon from "@mui/icons-material/CenterFocusStrongOutlined";
import { useNotifier } from "../../../Core/Notifier";
import { IAssessmentFocusArea } from "../../../Api/Interface/api.interface";
import {
  useCreateAssessmentFocusAreaMutation,
  useUpdateAssessmentFocusAreaMutation,
} from "../../../Api/attoDeskApi";

interface Props {
  open: boolean;
  onClose: () => void;
  data?: IAssessmentFocusArea | null;
}

const FocusAreaModal = ({ open, onClose, data }: Props) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { showMessage, showErrorMessage } = useNotifier();

  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [touched, setTouched] = useState(false);

  const [create, { isLoading: creating }] = useCreateAssessmentFocusAreaMutation();
  const [update, { isLoading: updating }] = useUpdateAssessmentFocusAreaMutation();
  const isEdit = !!data?.id;
  const saving = creating || updating;

  useEffect(() => {
    if (open) {
      setName(data?.name || "");
      setNotes(data?.notes || "");
      setIsActive(data?.is_active ?? true);
      setTouched(false);
    }
  }, [open, data]);

  const handleSave = async () => {
    setTouched(true);
    if (!name.trim()) return;
    try {
      const body = { name: name.trim(), notes: notes.trim() || null, is_active: isActive };
      const res: any = isEdit
        ? await update({ id: data!.id, body }).unwrap()
        : await create(body).unwrap();
      if (res.status) {
        showMessage(res.message || "Saved successfully");
        onClose();
      } else {
        showErrorMessage(res.message || "Failed to save");
      }
    } catch {
      showErrorMessage("Something went wrong");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden" } }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          pt: 2.75,
          pb: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: `linear-gradient(180deg, ${alpha(primary, 0.06)}, transparent)`,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "12px",
            flexShrink: 0,
            bgcolor: alpha(primary, 0.1),
            color: primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CenterFocusStrongOutlinedIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.01em" }}>
            {isEdit ? "Edit Focus Area" : "New Focus Area"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            Configure an assessment focus area
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ alignSelf: "flex-start" }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <Divider />

      {/* Body */}
      <Box sx={{ px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Box>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "text.secondary", mb: 0.75 }}>
            Focus Area Name{" "}
            <Typography component="span" sx={{ color: "error.main" }}>
              *
            </Typography>
          </Typography>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="e.g. Communication"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
            }}
            error={touched && !name.trim()}
            helperText={touched && !name.trim() ? "Name is required" : undefined}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "text.secondary", mb: 0.75 }}>
            Notes
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            placeholder="Add an optional note about this focus area"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.75,
            borderRadius: "12px",
            border: "1px solid rgba(0,0,0,0.1)",
            bgcolor: alpha(isActive ? "#22c55e" : "#94a3b8", 0.05),
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Status</Typography>
            <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
              {isActive ? "Active — available for use" : "Inactive — hidden from selection"}
            </Typography>
          </Box>
          <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="success" />
        </Box>
      </Box>
      <Divider />

      {/* Footer */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 3, py: 2, bgcolor: "rgba(0,0,0,0.015)" }}>
        <Button
          variant="text"
          size="small"
          onClick={onClose}
          sx={{ textTransform: "none", fontSize: 12.5, borderRadius: "10px", color: "text.secondary", px: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={saving || !name.trim()}
          sx={{ textTransform: "none", fontSize: 12.5, fontWeight: 600, borderRadius: "10px", boxShadow: "none", px: 3.5 }}
        >
          {saving ? <CircularProgress size={16} color="inherit" /> : "Save"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default FocusAreaModal;
